/**
 * Trip Detection Service
 * 
 * Detects trips from email bookings without requiring a tripId.
 * Groups bookings by date and location to suggest new trips.
 * Supports re-sync to match new bookings to existing trips.
 */

import {httpsCallable, functions} from "@/lib/functions";
import {db} from "@/lib/firebase";
import {doc, onSnapshot, collection, query, where, getDocs, type Unsubscribe} from "firebase/firestore";
import { withAuthRetry } from '@/services/authSessionRecovery';
import type {ExtractedBooking} from "./inboxSync";
import type {Trip} from "./trips";

export type DetectedTrip = {
  suggestedName: string;
  startDate: string; // ISO date string (YYYY-MM-DD)
  endDate: string; // ISO date string (YYYY-MM-DD)
  location?: string;
  bookings: Array<Omit<ExtractedBooking, "checkInDate" | "checkOutDate" | "departureDate" | "arrivalDate" | "rawEmailDate"> & {
    checkInDate?: string;
    checkOutDate?: string;
    departureDate?: string;
    arrivalDate?: string;
    rawEmailDate?: string;
  }>;
  confidence: number; // 0-1
  emailSubjects: string[];
};

type DetectTripsRequest = {
  provider?: "gmail" | "outlook" | "icloud";
  maxResults?: number;
  lookbackDays?: number;
};

type DetectTripsResponse = {
  success: boolean;
  trips: DetectedTrip[];
  totalBookings: number;
  provider?: "gmail" | "outlook" | "icloud";
  requiresAuth?: boolean;
  message?: string;
  error?: string;
};

const callDetectTrips = httpsCallable<DetectTripsRequest, DetectTripsResponse>(
  functions,
  "detectTripsFromEmail",
);

const callQueueTripDetection = httpsCallable<DetectTripsRequest, { taskId: string; status: string }>(
  functions,
  "queueTripDetection",
);

export type DetectionTaskStatus = {
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number;
  message?: string;
  trips?: DetectedTrip[];
  totalBookings?: number;
  error?: string;
  errorCode?: 'AUTH_PENDING' | 'NO_EMAILS_FOUND' | 'NO_BOOKINGS_EXTRACTED' | 'UNKNOWN_ERROR';
  diagnosticInfo?: {
    messagesFound?: number;
    bookingsExtracted?: number;
    provider?: string;
  };
};

/**
 * Detect trips from email bookings
 * 
 * @param options - Detection options
 * @returns Array of detected trip suggestions
 */
export async function detectTripsFromEmail(
  options?: DetectTripsRequest
): Promise<DetectedTrip[]> {
  try {
    const response = await withAuthRetry(() => callDetectTrips(options || {}));
    
    if (!response.data.success) {
      if (response.data.requiresAuth) {
        throw new Error(response.data.message || "Authentication required");
      }
      throw new Error(response.data.error || "Failed to detect trips");
    }

    return response.data.trips || [];
  } catch (error) {
    console.error("[TripDetection] Error detecting trips:", error);
    throw error;
  }
}

/**
 * Queue trip detection task (async processing)
 * Returns taskId immediately, processing happens in background
 * 
 * @param options - Detection options
 * @param onProgress - Optional callback for progress updates
 * @returns Promise that resolves when task is queued, with taskId
 */
export async function queueTripDetection(
  options?: DetectTripsRequest,
  onProgress?: (status: DetectionTaskStatus) => void
): Promise<{ taskId: string; unsubscribe: () => void }> {
  try {
    // Queue the task
    const response = await withAuthRetry(() => callQueueTripDetection(options || {}));
    const taskId = response.data.taskId;

    return subscribeToTripDetectionTask(taskId, onProgress);
  } catch (error) {
    console.error("[TripDetection] Error queueing trip detection:", error);
    throw error;
  }
}

export async function subscribeToTripDetectionTask(
  taskId: string,
  onProgress?: (status: DetectionTaskStatus) => void
): Promise<{ taskId: string; unsubscribe: () => void }> {
  try {
    // Subscribe to task updates using Firestore v9 modular API
    // IMPORTANT: db must use the SAME Firestore instance as the functions:
    // - If functions run in emulator, tasks are created in emulator Firestore → client must use emulator
    // - If functions run in production, tasks are created in production Firestore → client must use production
    // The client's Firestore connection is configured in src/lib/firebase.ts based on useFunctionsEmulator config
    
    // Get Firestore connection details for debugging
    const settings = (db as any)?._delegate?._settings ?? (db as any)?._settings;
    const firestoreHost: string = settings?.host || 'unknown';
    const firestoreSsl: boolean | undefined = settings?.ssl;
    const isUsingEmulator =
      firestoreSsl === false ||
      (typeof firestoreHost === 'string' &&
        firestoreHost !== 'unknown' &&
        firestoreHost !== 'firestore.googleapis.com');
    
    console.log('[TripDetection] Subscribing to task document:', {
      taskId,
      dbProjectId: db.app.options.projectId,
      isUsingEmulator,
      firestoreHost,
    });
    
    const docRef = doc(db, 'detectionTasks', taskId);
    
    // Add a delay and retry mechanism to handle eventual consistency
    // The document might not be immediately available after creation
    let retryCount = 0;
    const maxRetries = 5;
    const retryDelays = [200, 500, 1000, 2000, 3000]; // Exponential backoff
    
    // Try to get the document first to ensure it exists
    const { getDoc } = await import('firebase/firestore');
    let documentFound = false;
    
    for (let i = 0; i < maxRetries; i++) {
      await new Promise(resolve => setTimeout(resolve, retryDelays[i] || 3000));
      try {
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          documentFound = true;
          console.log(`[TripDetection] ✅ Task document found after ${i + 1} attempt(s)`);
          break;
        } else {
          console.log(`[TripDetection] Task document not found, retrying... (attempt ${i + 1}/${maxRetries})`, {
            taskId,
            fromCache: snapshot.metadata.fromCache,
            hasPendingWrites: snapshot.metadata.hasPendingWrites,
          });
        }
      } catch (error) {
        console.warn(`[TripDetection] Error fetching task document (attempt ${i + 1}/${maxRetries}):`, error);
        // Continue retrying
      }
    }
    
    if (!documentFound) {
      console.warn('[TripDetection] Task document not found after retries:', {
        taskId,
        dbProjectId: db.app.options.projectId,
        isUsingEmulator,
        firestoreHost: (db as any)._delegate?._settings?.host || 'unknown',
        suggestion: isUsingEmulator 
          ? 'Task may be in production Firestore. Check if functions are running in production mode.'
          : 'Task may be in emulator Firestore. Check if functions are running in emulator mode and client is connected to emulator.',
      });
      // Still subscribe - the document might appear later
      // Send initial progress update to show we're waiting
      onProgress?.({
        status: 'queued',
        progress: 0,
        message: 'Waiting for task to start...',
      });
    }

    let stallTimeoutId: ReturnType<typeof setTimeout> | undefined;
    let initTimeoutId: ReturnType<typeof setTimeout> | undefined;

    let hasReceivedUpdate = false;

    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      const data = snapshot.data();
      if (!data) {
        if (!hasReceivedUpdate) {
          console.warn('[TripDetection] Task document not found on first snapshot:', {
          taskId,
          snapshotExists: snapshot.exists(),
          snapshotMetadata: snapshot.metadata,
          dbProjectId: db.app.options.projectId,
          snapshotFromCache: snapshot.metadata.fromCache,
        });
          // Send progress update to show we're waiting
          onProgress?.({
            status: 'queued',
            progress: 0,
            message: 'Waiting for task to start...',
          });
        }
        return;
      }
      
      // Clear timeout once we receive an update
      if (!hasReceivedUpdate) {
        if (initTimeoutId !== undefined) {
          clearTimeout(initTimeoutId);
          initTimeoutId = undefined;
        }
        hasReceivedUpdate = true;
      }

      if (data.status === 'completed' || data.status === 'failed') {
        if (stallTimeoutId !== undefined) {
          clearTimeout(stallTimeoutId);
          stallTimeoutId = undefined;
        }
      }

      const status: DetectionTaskStatus = {
        status: data.status,
        progress: data.progress,
        message: data.message,
        trips: data.trips,
        totalBookings: data.totalBookings,
        error: data.error,
        errorCode: data.errorCode,
        diagnosticInfo: data.diagnosticInfo,
      };

      console.log('[TripDetection] Task status update:', {
        taskId,
        status: status.status,
        progress: status.progress,
        message: status.message,
        tripsCount: status.trips?.length || 0,
      });

      onProgress?.(status);
    }, (error) => {
      if (stallTimeoutId !== undefined) {
        clearTimeout(stallTimeoutId);
        stallTimeoutId = undefined;
      }
      if (initTimeoutId !== undefined) {
        clearTimeout(initTimeoutId);
        initTimeoutId = undefined;
      }
      console.error('[TripDetection] Error subscribing to task updates:', error);
      // If permission denied, try to provide helpful error
      if (error?.code === 'permission-denied') {
        console.error('[TripDetection] Permission denied reading detection task. Check Firestore rules for detectionTasks collection.');
        onProgress?.({
          status: 'failed',
          progress: 0,
          message: 'Permission denied. Please check your account permissions.',
          error: 'Permission denied reading detection task',
        });
      } else {
      // Call progress callback with error status
      onProgress?.({
        status: 'failed',
        progress: 0,
          message: 'Failed to read task status. Please try again.',
        error: error?.message || 'Unknown error',
      });
      }
    });

    const originalUnsubscribe = unsubscribe;
    const enhancedUnsubscribe = () => {
      if (initTimeoutId !== undefined) {
        clearTimeout(initTimeoutId);
        initTimeoutId = undefined;
      }
      if (stallTimeoutId !== undefined) {
        clearTimeout(stallTimeoutId);
        stallTimeoutId = undefined;
      }
      originalUnsubscribe();
    };

    initTimeoutId = setTimeout(() => {
      if (stallTimeoutId !== undefined) {
        clearTimeout(stallTimeoutId);
        stallTimeoutId = undefined;
      }
      console.error('[TripDetection] Timeout waiting for task document to appear:', {
        taskId,
        dbProjectId: db.app.options.projectId,
        isUsingEmulator,
        firestoreHost: ((db as any)?._delegate?._settings ?? (db as any)?._settings)?.host || 'unknown',
        possibleIssue: isUsingEmulator
          ? 'Client using emulator but task may be in production Firestore. Check function deployment mode.'
          : 'Client using production but task may be in emulator Firestore. Check if functions emulator is running.',
      });
      onProgress?.({
        status: 'failed',
        progress: 0,
        message: 'Task initialization timed out. The task may have been created in a different Firestore instance. Please try again.',
        error: 'Task document not found after timeout',
      });
      enhancedUnsubscribe();
    }, 30000);

    stallTimeoutId = setTimeout(() => {
      stallTimeoutId = undefined;
      console.error('[TripDetection] Detection stalled (no terminal status in time)', { taskId });
      onProgress?.({
        status: 'failed',
        progress: 0,
        message: 'Trip detection is taking too long. Please try again.',
        error: 'Detection timed out',
      });
      enhancedUnsubscribe();
    }, 3 * 60 * 1000);

    return { taskId, unsubscribe: enhancedUnsubscribe };
  } catch (error) {
    console.error("[TripDetection] Error subscribing to trip detection task:", error);
    throw error;
  }
}

/**
 * Check if trip detection requires authentication
 */
export async function checkTripDetectionAuth(
  provider?: "gmail" | "outlook" | "icloud"
): Promise<{requiresAuth: boolean; message?: string}> {
  try {
    const response = await callDetectTrips({provider, maxResults: 1});
    
    return {
      requiresAuth: response.data.requiresAuth || false,
      message: response.data.message,
    };
  } catch (error) {
    console.error("[TripDetection] Error checking auth:", error);
    return {requiresAuth: true, message: "Error checking authentication"};
  }
}

/**
 * Match result for a booking to an existing trip
 */
export type BookingTripMatch = {
  booking: DetectedTrip['bookings'][0];
  matchedTrip: {
    tripId: string;
    tripName: string;
    startDate: string;
    endDate: string;
    destination?: string;
  } | null;
  score: number; // 0-1, how confident the match is
  isNewTrip: boolean; // If true, this booking should create a new trip
};

/**
 * Result of categorizing detected trips for re-sync
 */
export type CategorizedDetection = {
  newTrips: DetectedTrip[]; // Trips that should be created as new
  existingTripMatches: Array<{
    trip: {
      tripId: string;
      tripName: string;
      startDate: string;
      endDate: string;
      destination?: string;
    };
    bookings: DetectedTrip['bookings'];
    detectedTripName: string; // Original suggested name from detection
  }>; // Bookings that match existing trips
};

/**
 * Fetch user's existing trips for matching
 */
async function fetchUserTripsForMatching(userId: string): Promise<Trip[]> {
  try {
    const tripsRef = collection(db, 'trips');
    const ownerQuery = query(tripsRef, where('owner', '==', userId));
    const memberQuery = query(tripsRef, where('members', 'array-contains', userId));
    
    const [ownerSnapshot, memberSnapshot] = await Promise.all([
      getDocs(ownerQuery),
      getDocs(memberQuery)
    ]);
    
    const tripsMap = new Map<string, Trip>();
    
    // Helper to convert Firestore data to Trip
    const convertToTrip = (docId: string, data: any): Trip => {
      const startDate = data.startDate?.toDate?.() || new Date(data.startDate);
      const endDate = data.endDate?.toDate?.() || new Date(data.endDate);
      
      return {
        id: docId,
        name: data.title || data.name || 'Untitled Trip',
        startDate,
        endDate,
        destination: data.destination,
        owner: data.owner,
        members: data.members || [],
        status: data.status,
      };
    };
    
    ownerSnapshot.forEach((doc) => {
      tripsMap.set(doc.id, convertToTrip(doc.id, doc.data()));
    });
    
    memberSnapshot.forEach((doc) => {
      if (!tripsMap.has(doc.id)) {
        tripsMap.set(doc.id, convertToTrip(doc.id, doc.data()));
      }
    });
    
    return Array.from(tripsMap.values());
  } catch (error) {
    console.error('[TripDetection] Error fetching user trips:', error);
    return [];
  }
}

/**
 * Calculate date overlap score between a booking and a trip
 */
function calculateDateOverlapScore(
  bookingStartDate: string | undefined,
  bookingEndDate: string | undefined,
  tripStartDate: Date,
  tripEndDate: Date
): number {
  if (!bookingStartDate) return 0;
  
  const bookingStart = new Date(bookingStartDate);
  const bookingEnd = bookingEndDate ? new Date(bookingEndDate) : bookingStart;
  
  // Add buffer days for bookings that might be slightly outside trip range
  const bufferDays = 7;
  const bufferMs = bufferDays * 24 * 60 * 60 * 1000;
  const adjustedTripStart = new Date(tripStartDate.getTime() - bufferMs);
  const adjustedTripEnd = new Date(tripEndDate.getTime() + bufferMs);
  
  // Check if booking falls within adjusted trip range
  if (bookingStart >= adjustedTripStart && bookingStart <= adjustedTripEnd) {
    // Perfect match within trip dates
    if (bookingStart >= tripStartDate && bookingStart <= tripEndDate) {
      return 1.0;
    }
    // Within buffer range
    return 0.8;
  }
  
  // Check if dates are close (within 14 days)
  const daysBefore = (tripStartDate.getTime() - bookingStart.getTime()) / (1000 * 60 * 60 * 24);
  const daysAfter = (bookingStart.getTime() - tripEndDate.getTime()) / (1000 * 60 * 60 * 24);
  
  if (daysBefore > 0 && daysBefore <= 14) return 0.6;
  if (daysAfter > 0 && daysAfter <= 14) return 0.6;
  
  return 0;
}

/**
 * Calculate location match score between a booking and a trip
 */
function calculateLocationMatchScore(
  bookingLocation: string | undefined,
  tripDestination: string | undefined
): number {
  // Missing locations should not contribute to an auto-match.
  if (!bookingLocation || !tripDestination) return 0;
  
  const normalizeLocation = (loc: string): string => {
    return loc.toLowerCase()
      .replace(/,\s*/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  const bookingNorm = normalizeLocation(bookingLocation);
  const tripNorm = normalizeLocation(tripDestination);
  
  // Exact match
  if (bookingNorm === tripNorm) return 1.0;
  
  // Contains match (e.g., "Tokyo" in "Tokyo, Japan")
  if (bookingNorm.includes(tripNorm) || tripNorm.includes(bookingNorm)) return 0.9;
  
  // Check if first word matches (city name)
  const bookingCity = bookingNorm.split(' ')[0];
  const tripCity = tripNorm.split(' ')[0];
  if (bookingCity === tripCity && bookingCity.length > 2) return 0.8;
  
  return 0;
}

/**
 * Match a single booking to existing trips
 */
function matchBookingToTrips(
  booking: DetectedTrip['bookings'][0],
  existingTrips: Trip[]
): { trip: Trip | null; score: number } {
  let bestMatch: Trip | null = null;
  let bestScore = 0;
  
  const bookingDate = booking.departureDate || booking.checkInDate || booking.arrivalDate || booking.checkOutDate;
  
  for (const trip of existingTrips) {
    // Require locations for auto-matching; otherwise skip to avoid date-only matches.
    if (!booking.location || !trip.destination) {
      continue;
    }

    // Calculate date score
    const dateScore = calculateDateOverlapScore(
      bookingDate,
      booking.arrivalDate || booking.checkOutDate,
      trip.startDate,
      trip.endDate
    );
    
    // Calculate location score
    const locationScore = calculateLocationMatchScore(
      booking.location,
      trip.destination
    );

    // Hard gate: if we have both locations and the score is weak, do not match.
    if (locationScore < 0.5) {
      continue;
    }

    // Hard gate: never match on location alone (e.g. same city, different months).
    // Booking must overlap the trip window, sit in the ±7d buffer, or be within ±14d
    // of the trip span — same rules as calculateDateOverlapScore.
    if (dateScore <= 0) {
      continue;
    }
    
    // Combined score: prioritize location over date
    const combinedScore = (dateScore * 0.3) + (locationScore * 0.7);
    
    // Only consider matches with score >= 0.7
    if (combinedScore >= 0.7 && combinedScore > bestScore) {
      bestMatch = trip;
      bestScore = combinedScore;
    }
  }
  
  return { trip: bestMatch, score: bestScore };
}

/**
 * Categorize detected trips into new trips vs. matches to existing trips
 * 
 * @param detectedTrips - Trips detected from email sync
 * @param userId - Current user ID
 * @returns Categorized detection result
 */
export async function categorizeDetectedTrips(
  detectedTrips: DetectedTrip[],
  userId: string
): Promise<CategorizedDetection> {
  // Fetch existing trips for the user
  const existingTrips = await fetchUserTripsForMatching(userId);
  
  if (existingTrips.length === 0) {
    // No existing trips, all detected trips are new
    return {
      newTrips: detectedTrips,
      existingTripMatches: [],
    };
  }
  
  const newTrips: DetectedTrip[] = [];
  const existingTripMatches: Map<string, { 
    trip: Trip; 
    bookings: DetectedTrip['bookings'];
    detectedTripName: string;
  }> = new Map();
  
  for (const detectedTrip of detectedTrips) {
    const matchedBookings: DetectedTrip['bookings'] = [];
    const unmatchedBookings: DetectedTrip['bookings'] = [];
    
    for (const booking of detectedTrip.bookings) {
      const { trip, score } = matchBookingToTrips(booking, existingTrips);
      
      if (trip && score >= 0.7) {
        // This booking matches an existing trip (same threshold as matchBookingToTrips)
        const existingMatch = existingTripMatches.get(trip.id);
        if (existingMatch) {
          existingMatch.bookings.push(booking);
        } else {
          existingTripMatches.set(trip.id, {
            trip,
            bookings: [booking],
            detectedTripName: detectedTrip.suggestedName,
          });
        }
        matchedBookings.push(booking);
      } else {
        unmatchedBookings.push(booking);
      }
    }
    
    // If there are unmatched bookings, create a new trip for them
    if (unmatchedBookings.length > 0) {
      newTrips.push({
        ...detectedTrip,
        bookings: unmatchedBookings,
      });
    }
  }
  
  // Convert Map to array for return
  const existingTripMatchesArray = Array.from(existingTripMatches.values()).map(match => ({
    trip: {
      tripId: match.trip.id,
      tripName: match.trip.name,
      startDate: match.trip.startDate.toISOString().split('T')[0],
      endDate: match.trip.endDate.toISOString().split('T')[0],
      destination: match.trip.destination,
    },
    bookings: match.bookings,
    detectedTripName: match.detectedTripName,
  }));
  
  console.log('[TripDetection] Categorized detection result:', {
    newTripsCount: newTrips.length,
    existingTripMatchesCount: existingTripMatchesArray.length,
    totalBookingsForExisting: existingTripMatchesArray.reduce((sum, m) => sum + m.bookings.length, 0),
  });
  
  return {
    newTrips,
    existingTripMatches: existingTripMatchesArray,
  };
}

